import { useScrollFadeIn, fadeCls } from "@/hooks/useScrollFadeIn";
import { useTranslation } from "react-i18next";
import { Quote } from "lucide-react";

/**
 * NordicResearchProof
 * -------------------
 * Forsknings-baseret social proof. Citater + Frode Dales 4 udfordringer
 * + 100-dages tidslinje. Hentet fra "Navigering i Ledelsesovergangen"
 * (Møller Institute, Frode Dale, Lederweb, Lunds universitet, Theseus FI).
 *
 * Designet til at sidde mellem RelarkPainPoint og RelarkSixFactors —
 * agitation først, så "du er ikke alene + her er strukturen".
 */

const QUOTES = [
  {
    text: "Chefer er ofte mere stressede end deres medarbejdere.",
    source: "Chef.se, 2025 — svensk chefsundersøgelse",
  },
  {
    text: "I stressede perioder flygter den nye leder tilbage til specialistopgaverne.",
    source: "Heidi Helavirta, Theseus FI",
  },
  {
    text: "At lede mennesker er den sværeste disciplin.",
    source: "Frode Dale, nyilederrollen.no",
  },
];

const FOUR_CHALLENGES = [
  { title: "Lede en organisation", body: "Fra ansat til arbejdsgiverrepræsentant. Svære prioriteringer mellem budget og mennesker." },
  { title: "Lede mennesker", body: "Motivere, håndtere konflikter, sikre respekt — samtidig med at resultaterne kommer." },
  { title: "Kende sig selv", body: "Selvindsigt og selvregulering. Brug dig selv som instrument uden at lade usikkerhed skygge." },
  { title: "Håndtere stress og følelser", body: "Frygt for at fejle. Ensomhed. Pres fra både team og chef. Få er forberedt." },
];

const TIMELINE = [
  { weeks: "Uge 1-4", phase: "Lyt", body: "1:1 med alle. Spørg hvad der virker, før du ændrer noget." },
  { weeks: "Uge 5-8", phase: "Analysér", body: "Kortlæg interessenter. Afstem forventninger med din egen chef." },
  { weeks: "Uge 9-12", phase: "Beslut", body: "Præsentér din analyse. Find quick wins. Inviter teamet ind i løsningen." },
  { weeks: "Uge 13-14", phase: "Forankr", body: "Sæt mål for næste halve år. Etablér faste sparringsrutiner." },
];

export default function NordicResearchProof() {
  const { ref, isVisible } = useScrollFadeIn();
  const { i18n } = useTranslation();
  const isDanish = i18n.language?.startsWith("da");

  // Vi viser kun den tunge danske version på da/nb/sv (samme læsbarhed).
  // På andre sprog skipper vi sektionen for nu (kommer i transcreation).
  const showFullSection = isDanish || i18n.language?.startsWith("nb") || i18n.language?.startsWith("sv");
  if (!showFullSection) return null;

  return (
    <section className="bg-background py-16 sm:py-24">
      <div ref={ref} className={`mx-auto max-w-5xl px-5 ${fadeCls(isVisible)}`}>
        <div className="text-center mb-12">
          <p className="text-xs uppercase tracking-wider text-muted-foreground mb-3">
            Baseret på nordisk forskning
          </p>
          <h2 className="text-2xl sm:text-3xl font-bold mb-3">
            Du er ikke alene. Forskningen kender mønsteret.
          </h2>
          <p className="text-sm sm:text-base text-muted-foreground max-w-2xl mx-auto">
            Møller Institute, Frode Dale, Lederweb og Lunds universitet beskriver alle den samme transition.
            Det er ikke svaghed. Det er en kendt læringskurve — og den kan struktureres.
          </p>
        </div>

        {/* Citater */}
        <div className="grid gap-4 sm:grid-cols-3 mb-16">
          {QUOTES.map((q) => (
            <figure
              key={q.source}
              className="rounded-2xl border border-border bg-card p-6 flex flex-col"
            >
              <Quote className="h-5 w-5 text-muted-foreground mb-3" aria-hidden="true" />
              <blockquote className="text-base font-medium leading-snug flex-1">
                {q.text}
              </blockquote>
              <figcaption className="mt-4 text-xs text-muted-foreground">
                {q.source}
              </figcaption>
            </figure>
          ))}
        </div>

        {/* 4 udfordringer */}
        <div className="mb-16">
          <h3 className="text-lg sm:text-xl font-semibold mb-1 text-center">
            De fire ting du skal lære samtidig
          </h3>
          <p className="text-xs text-muted-foreground text-center mb-6">
            Frode Dale — "Å være ny som førstegangsleder"
          </p>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {FOUR_CHALLENGES.map((c, i) => (
              <div
                key={c.title}
                className="rounded-xl border border-border bg-card p-5"
              >
                <div className="text-xs font-mono text-muted-foreground mb-2">
                  0{i + 1}
                </div>
                <h4 className="font-semibold text-sm mb-2">{c.title}</h4>
                <p className="text-xs text-muted-foreground leading-relaxed">{c.body}</p>
              </div>
            ))}
          </div>
        </div>

        {/* 100-dages tidslinje */}
        <div>
          <h3 className="text-lg sm:text-xl font-semibold mb-1 text-center">
            Sådan strukturerer du de første 100 dage
          </h3>
          <p className="text-xs text-muted-foreground text-center mb-6">
            Lederweb / DSR Lederforening — 3-fase modellen
          </p>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {TIMELINE.map((t) => (
              <div
                key={t.weeks}
                className="rounded-xl border border-border bg-card p-5"
              >
                <div className="text-xs font-mono text-muted-foreground mb-1">
                  {t.weeks}
                </div>
                <h4 className="font-semibold text-sm mb-2">{t.phase}</h4>
                <p className="text-xs text-muted-foreground leading-relaxed">{t.body}</p>
              </div>
            ))}
          </div>
        </div>

        <p className="mt-12 text-center text-xs text-muted-foreground max-w-2xl mx-auto">
          Relark er bygget oven på denne forskning. Vores AI-coach kan citere kilderne direkte
          når du spørger — fordi sturdy, calm rådgivning kræver mere end gode vibes.
        </p>
      </div>
    </section>
  );
}